import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { type CameraFlyThroughControls, CameraViewType } from '@strava/mre';

import {
  DEFAULT_CAMERA_MARGINS,
  FLYOVER_PREVIEW_DURATION,
  PLAY_PAUSE_ICON_DISPLAY_TIME
} from '../../constants';
import { getWorldBounds2FromLatLngArray } from '../../helpers/getWorldBounds2FromLatLngArray';
import { getWorldPoint3ArrayFromLatLngElevationArray } from '../../helpers/getWorldPoint3ArrayFromLatLngElevationArray';
import { mergeCoordinatesAndElevation } from '../../helpers/mergeCoordinatesAndElevation';
import { FlyoverContext } from '../../hooks/useFlyoverState';
import { useMRE } from '../../hooks/useMRE';
import { useMREState } from '../../hooks/useMREState';
import type { FlyoverPlaybackState } from '../../types';
import { FlyoverSpeedControl } from '../FlyoverSpeedControl/FlyoverSpeedControl';
import { PlayPauseIcon } from '../PlayPauseIcon/PlayPauseIcon';

export interface FlyoverProps {
  polyline: Array<[number, number]>;
  elevation: Array<[number, number]>;
  isPreview?: boolean; // If true, the flyover will only show a preview
  onPreviewEnd?: () => void; // if isPreview, this callback will be called at end of the preview
  playbackSpeedFactor?: number;
  onProgressChanged?: (progress: number) => void;
}

export const Flyover = ({
  polyline,
  elevation,
  isPreview = false, // Default to false if not provided
  onPreviewEnd,
  playbackSpeedFactor = 1.6,
  onProgressChanged
}: FlyoverProps) => {
  const [isCameraReady, setIsCameraReady] = useState(false);
  const { terrainEngine, track } = useMRE();
  const {
    isFlyoverInProgress,
    setIsFlyoverInProgress,
    resetCameraAndContent,
    flyoverEndTargetRef,
    cameraViewType,
    setCameraViewType
  } = useMREState();

  useEffect(() => {
    if (isFlyoverInProgress) {
      // Even though .flyover will set the camera to 3D mode, we need to set it here
      // to ensure that any camera transitions prior to starting the flyover (e.g. flyToBounds)
      // are also in 3D mode, and that the camera elevation is not 0 (which would be the case in 2D mode).
      setCameraViewType(CameraViewType.CAMERA3D);
    } else {
      // This will get called when exiting flyover mode
      setIsCameraReady(false);
    }
  }, [isFlyoverInProgress, setCameraViewType]);

  useEffect(() => {
    // Only if we've set the camera to 3D mode before
    if (isFlyoverInProgress && cameraViewType === CameraViewType.CAMERA3D) {
      // We need to fly the camera to the bounds of the polyline in 3D mode (different bounds)
      // before starting the flyover. This + manually setting the camera to 3D mode above
      // fixes the issue where the camera would start the flyover below the terrain.
      terrainEngine
        ?.getCamera()
        .flyToBounds(
          getWorldBounds2FromLatLngArray(polyline),
          0,
          0,
          0.005,
          DEFAULT_CAMERA_MARGINS,
          {
            onActionComplete: () => {
              setIsCameraReady(true);
            }
          }
        );
    }
  }, [terrainEngine, polyline, isFlyoverInProgress, cameraViewType]);

  // Relied upon within useEffects, but we do not want a value update to trigger re-run of useEffects.
  const playbackSpeedMultiplier = useRef(1);

  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false);

  const [flyoverHandle, setFlyoverHandle] =
    useState<CameraFlyThroughControls>();

  const [playbackState, setPlaybackState] =
    useState<FlyoverPlaybackState>('playing'); // for now we only support starting with auto-play

  const [progress, setProgress] = useState<number>(0);

  const path = useMemo(() => {
    return mergeCoordinatesAndElevation(polyline, elevation);
  }, [polyline, elevation]);

  useEffect(() => {
    if (terrainEngine && isFlyoverInProgress && isPreview) {
      // After FLYOVER_PREVIEW_DURATION
      const flyoverPreviewTimeoutId = setTimeout(() => {
        resetCameraAndContent(); // Reset camera and content to original state
        setIsFlyoverInProgress(false); // Exit flyover mode
        onPreviewEnd?.(); // Call callback if provided
      }, FLYOVER_PREVIEW_DURATION);

      return () => clearTimeout(flyoverPreviewTimeoutId); // Clear the timeout if the component unmounts
    }
  }, [
    terrainEngine,
    isFlyoverInProgress,
    isPreview,
    setIsFlyoverInProgress,
    onPreviewEnd,
    resetCameraAndContent
  ]);

  const startFlyover = useCallback(
    (
      options: { paused: boolean; progress: number } = {
        paused: false,
        progress: 0
      }
    ) => {
      if (!terrainEngine || !path || !playbackSpeedFactor || !isCameraReady) {
        return;
      }

      setPlaybackState(options.paused ? 'paused' : 'playing');

      // We filter duplicate points out of the the flyover track before passing it to MRE
      const track = path.filter((curr, index) => {
        if (index === 0) {
          return true;
        }

        // cast to prevent compiler "possibly undefined" error, which we know can't be the case based on index === 0
        // check above
        const last = path[index - 1] as [number, number, number];

        return last[0] !== curr[0] || last[1] !== curr[1];
      });

      // If endTarget is provided, use it to set the camera target at the end of the flyover
      // Otherwise, set to undefined
      const cameraEndTarget = flyoverEndTargetRef?.current
        ? {
            avoidMountains: true,
            cameraTarget: flyoverEndTargetRef.current
          }
        : undefined;

      const newHandle = terrainEngine.getCamera().flyover(
        true, // "isInteractive" - enables detachable camera, but is also required for speed control etc.
        getWorldPoint3ArrayFromLatLngElevationArray(track),
        // HACK: we do want to set extra margin at the bottom so that the puck tends to not sit behind
        // the speed controls. However, we don't really need extra margin at the top. However, unless we
        // set it, we end up with the route being shifted up above center when cameraEndTarget is set.
        // This seems to work well as it is now, but may need revisited if the engine logic is tweaked.
        { ...DEFAULT_CAMERA_MARGINS, bottom: 0.15, top: 0.15 },
        {
          onFlyThroughProgress: (_, trackProgress: number) => {
            setProgress(trackProgress);
          },
          onInterrupted: () => {
            /*
             * Docs:
             * Called if the flyThrough was interrupted before the animation finished (or was explicitly stopped)
             * with CameraFlyThroughControls#stop. Will be also immediately called if the flyThrough(Interactive)
             * method received bad data (line with less than 2 points or no matching styles found).
             *
             * Reaching here does not indicate an error.
             */
          }
        },

        cameraEndTarget,
        undefined,
        undefined
      );

      if (newHandle) {
        newHandle.setAnimationSpeedMultiplier(
          options.paused
            ? 0
            : playbackSpeedFactor * playbackSpeedMultiplier.current
        );

        if (options.progress) {
          newHandle.setTrackProgress(options.progress);
        }

        setFlyoverHandle(newHandle);
      }
    },
    [
      flyoverEndTargetRef,
      terrainEngine,
      path,
      playbackSpeedFactor,
      isCameraReady
    ]
  );

  const handleShowPlayPauseIcon = () => {
    setShowPlayPauseIcon(true);

    setTimeout(() => {
      setShowPlayPauseIcon(false);
    }, PLAY_PAUSE_ICON_DISPLAY_TIME);
  };

  const handlePause = useCallback(() => {
    setPlaybackState('paused');
    handleShowPlayPauseIcon();
    flyoverHandle?.setAnimationSpeedMultiplier(0);

    track({
      action: 'pause',
      element: 'flyover'
    });
  }, [flyoverHandle, track]);

  const handleResume = useCallback(() => {
    setPlaybackState('playing');
    handleShowPlayPauseIcon();
    flyoverHandle?.setAnimationSpeedMultiplier(
      playbackSpeedFactor * playbackSpeedMultiplier.current
    );
    track({
      action: 'resume',
      element: 'flyover'
    });
  }, [flyoverHandle, playbackSpeedFactor, track]);

  const handleRestart = useCallback(() => {
    startFlyover();

    track({
      action: 'restart',
      element: 'flyover'
    });
  }, [startFlyover, track]);

  const setPlaybackSpeedMultiplier = (value: number) => {
    playbackSpeedMultiplier.current = value;

    if (playbackState === 'playing') {
      flyoverHandle?.setAnimationSpeedMultiplier(playbackSpeedFactor * value);

      track({
        action: 'set_playback_speed_multiplier',
        element: 'flyover',
        properties: {
          playbackSpeedMultiplier: value
        }
      });
    } else {
      // do nothing - handleResume will be responsible for updating the engine
    }
  };

  const handleExternalSetProgress = (progress: number) => {
    // If updating progress from a 'finished' state, we want to pause the flyover
    if (playbackState === 'finished') {
      handlePause();
    }

    flyoverHandle?.setTrackProgress(progress);
  };

  // Autoplay
  useEffect(() => {
    if (isFlyoverInProgress) {
      // Reset playback speed multiplier to default when starting a new flyover
      playbackSpeedMultiplier.current = 1;
      startFlyover();
    }
  }, [startFlyover, isFlyoverInProgress]);

  // Stop animation on unmount
  useEffect(() => {
    return () => {
      // on unmount, we want to for sure make sure the flyover is finished
      flyoverHandle?.stop();
    };
  }, [flyoverHandle]);

  // track progress updates
  useEffect(() => {
    progress && onProgressChanged?.(progress);

    if (progress === 1) {
      setPlaybackState('finished');
    }
  }, [progress, onProgressChanged]);

  // Setup keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        e.preventDefault();

        if (playbackState === 'playing') {
          handlePause();
        } else if (playbackState === 'finished') {
          handleRestart();
        } else {
          handleResume();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePause, handleRestart, handleResume, playbackState]);

  if (!isFlyoverInProgress) {
    return null;
  }

  return (
    <FlyoverContext.Provider
      value={{
        playbackState,
        playbackSpeedMultiplier: playbackSpeedMultiplier.current,
        setPlaybackSpeedMultiplier,
        progress,
        setProgress: handleExternalSetProgress,
        pause: handlePause,
        resume: handleResume,
        restart: handleRestart
      }}
    >
      <>
        {/* Note: If the DOM structure changes here, this selector may need to be
      updated - https://github.com/strava/web/blob/main/apps/core/pages/activities/%5Bid%5D/flyover.module.scss#L22 */}
        <FlyoverSpeedControl />
        {showPlayPauseIcon && <PlayPauseIcon playbackState={playbackState} />}
      </>
    </FlyoverContext.Provider>
  );
};
